import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Lock, LogIn, AlertCircle, Users, Calendar, Phone, Mail,
  Tag, Filter, Download, ChevronDown, ChevronUp, MessageCircle,
  Clock, CheckCircle, XCircle, UserCheck, DollarSign, Loader2,
  ArrowRight, ExternalLink, TrendingUp, BarChart2, Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors and labels
const STATUS_CONFIG = {
  new: { label: 'New Lead', color: '#7c3aed', bgColor: 'bg-[#7c3aed]/10', borderColor: 'border-[#7c3aed]/20' },
  qualified_awaiting: { label: 'Qualified, awaiting time', color: '#F59E0B', bgColor: 'bg-[#F59E0B]/10', borderColor: 'border-[#F59E0B]/20' },
  booked: { label: 'Booked', color: '#06b6d4', bgColor: 'bg-[#06b6d4]/10', borderColor: 'border-[#06b6d4]/20' },
  completed: { label: 'Completed', color: '#10B981', bgColor: 'bg-[#10B981]/10', borderColor: 'border-[#10B981]/20' },
  no_show: { label: 'No-Show', color: '#EF4444', bgColor: 'bg-[#EF4444]/10', borderColor: 'border-[#EF4444]/20' },
  converted: { label: 'Converted', color: '#10B981', bgColor: 'bg-[#10B981]/20', borderColor: 'border-[#10B981]/30' },
  closed: { label: 'Closed', color: '#64748b', bgColor: 'bg-[#64748b]/10', borderColor: 'border-[#64748b]/20' },
};

const TAG_CONFIG = {
  priority: { label: 'Priority', color: '#F59E0B', emoji: '🔥' },
  standard: { label: 'Standard', color: '#06b6d4', emoji: '📋' },
  exploring: { label: 'Exploring', color: '#64748b', emoji: '👀' },
};

export default function AdminCallsPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [expandedLead, setExpandedLead] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTag, setFilterTag] = useState('');

  const getAuthHeader = () => {
    const savedAuth = localStorage.getItem('adminAuth');
    if (savedAuth) {
      return { 'Authorization': 'Basic ' + savedAuth };
    }
    return { 'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`) };
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/calls/stats`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        setIsAuthenticated(true);
        localStorage.setItem('adminAuth', btoa(`${credentials.username}:${credentials.password}`));
        fetchData();
      } else {
        setAuthError('Invalid username or password');
      }
    } catch (error) {
      setAuthError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/admin/calls/stats`, {
        headers: getAuthHeader()
      });
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      // Fetch leads
      let url = `${API_URL}/api/admin/calls/leads`;
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterTag) params.append('tag', filterTag);
      if (params.toString()) url += `?${params.toString()}`;

      const leadsRes = await fetch(url, {
        headers: getAuthHeader()
      });
      if (leadsRes.ok) {
        const data = await leadsRes.json();
        setLeads(data.leads || []);
      } else if (leadsRes.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem('adminAuth');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOutcome = async (leadId, outcome) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/calls/leads/${leadId}/outcome`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ outcome })
      });
      
      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Error updating outcome:', error);
    }
  };

  const updateNotes = async (leadId, notes) => {
    try {
      await fetch(`${API_URL}/api/admin/calls/leads/${leadId}/notes`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes })
      });
    } catch (error) {
      console.error('Error updating notes:', error);
    }
  };

  const setCallTime = async (leadId, callTime) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/calls/leads/${leadId}/call-time`, {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ call_time: callTime })
      });
      
      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Error setting call time:', error);
    }
  };

  const exportCSV = () => {
    window.open(`${API_URL}/api/admin/calls/export`, '_blank');
  };

  // Generate WhatsApp reminder message
  const generateWhatsAppReminder = (lead) => {
    const name = lead.name || 'there';
    const date = lead.booking?.date || '[date]';
    const time = lead.booking?.time || '[time]';
    const timezone = lead.booking?.timezone || 'IST';
    const joinLink = lead.booking?.meet_link || 'https://monkalgo.com';
    
    const message = lead.language === 'hindi'
      ? `नमस्ते ${name}, यह साहिल है Monk Algo से। आपकी Google Meet कॉल ${date} को ${time} (${timezone}) पर है। जॉइन लिंक: ${joinLink}`
      : `Hi ${name}, this is Sahil from Monk Algo. A reminder of our Google Meet call on ${date} at ${time} (${timezone}). Join link: ${joinLink}`;
    
    const phone = lead.phone?.replace('+', '').replace(' ', '') || '';
    return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  };

  // Check for saved auth on mount
  useEffect(() => {
    const savedAuth = localStorage.getItem('adminAuth');
    if (savedAuth) {
      fetch(`${API_URL}/api/admin/calls/stats`, {
        headers: { 'Authorization': 'Basic ' + savedAuth }
      })
        .then(res => {
          if (res.ok) {
            setIsAuthenticated(true);
            fetchData();
          } else {
            localStorage.removeItem('adminAuth');
          }
        })
        .catch(() => localStorage.removeItem('adminAuth'));
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [filterStatus, filterTag]);

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050508] flex items-center justify-center px-6 pt-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="glass-strong rounded-2xl p-8">
            <div className="flex items-center justify-center mb-6">
              <div className="w-14 h-14 rounded-xl bg-[#7c3aed]/15 flex items-center justify-center">
                <Phone className="w-7 h-7 text-[#7c3aed]" />
              </div>
            </div>
            
            <h1 className="font-heading font-bold text-2xl text-white text-center mb-2">Calls Admin</h1>
            <p className="font-body text-white/50 text-sm text-center mb-6">Manage booking leads and calls</p>

            {authError && (
              <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="font-body text-sm text-red-400">{authError}</span>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="font-body text-xs text-white/50 mb-1 block">Username</label>
                <input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white font-body focus:outline-none focus:border-[#7c3aed]/50"
                  placeholder="Enter username"
                  required
                  data-testid="calls-admin-username"
                />
              </div>
              
              <div>
                <label className="font-body text-xs text-white/50 mb-1 block">Password</label>
                <input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white font-body focus:outline-none focus:border-[#7c3aed]/50"
                  placeholder="Enter password"
                  required
                  data-testid="calls-admin-password"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-purple flex items-center justify-center gap-2 py-3"
                data-testid="calls-admin-login-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <LogIn size={16} />
                    Sign In
                  </>
                )}
              </button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050508] pt-24 pb-12 px-6">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="font-heading font-bold text-2xl text-white mb-1">Calls Dashboard</h1>
              <p className="font-body text-white/50 text-sm">Manage booking leads and track conversions</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={exportCSV}
                className="btn-secondary text-sm flex items-center gap-2"
                data-testid="export-csv-btn"
              >
                <Download size={14} />
                Export CSV
              </button>
              <button
                onClick={() => {
                  setIsAuthenticated(false);
                  localStorage.removeItem('adminAuth');
                }}
                className="btn-secondary text-sm"
                data-testid="calls-admin-logout"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="glass rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-[#7c3aed]" />
                  <span className="text-white/50 text-sm">Forms Started</span>
                </div>
                <div className="font-heading font-bold text-2xl text-white">{stats.forms_started || 0}</div>
                <div className="text-white/40 text-xs mt-1">This week: {stats.this_week || 0}</div>
              </div>
              <div className="glass rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="w-5 h-5 text-[#06b6d4]" />
                  <span className="text-white/50 text-sm">Calls Booked</span>
                </div>
                <div className="font-heading font-bold text-2xl text-white">{stats.calls_booked || 0}</div>
                <div className="text-white/40 text-xs mt-1">This month: {stats.this_month || 0}</div>
              </div>
              <div className="glass rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="w-5 h-5 text-[#10B981]" />
                  <span className="text-white/50 text-sm">Show Rate</span>
                </div>
                <div className="font-heading font-bold text-2xl text-[#10B981]">{stats.show_rate || 0}%</div>
              </div>
              <div className="glass rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="w-5 h-5 text-[#F59E0B]" />
                  <span className="text-white/50 text-sm">Conversion Rate</span>
                </div>
                <div className="font-heading font-bold text-2xl text-[#F59E0B]">{stats.conversion_rate || 0}%</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-6">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#7c3aed]/50"
              data-testid="filter-status"
            >
              <option value="">All Statuses</option>
              {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
            <select
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#7c3aed]/50"
              data-testid="filter-tag"
            >
              <option value="">All Tags</option>
              {Object.entries(TAG_CONFIG).map(([key, val]) => (
                <option key={key} value={key}>{val.emoji} {val.label}</option>
              ))}
            </select>
            {loading && <Loader2 className="w-5 h-5 text-[#7c3aed] animate-spin" />}
          </div>

          {/* Leads List */}
          <div className="space-y-4">
            {leads.length === 0 ? (
              <div className="glass rounded-xl p-8 text-center">
                <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50">No leads found</p>
              </div>
            ) : (
              leads.map((lead) => {
                const statusConfig = STATUS_CONFIG[lead.status] || STATUS_CONFIG.new;
                const tagConfig = TAG_CONFIG[lead.tag] || TAG_CONFIG.standard;
                const isExpanded = expandedLead === lead._id;
                
                return (
                  <motion.div
                    key={lead._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`glass rounded-xl border ${statusConfig.borderColor} overflow-hidden`}
                  >
                    {/* Lead Header */}
                    <div 
                      className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => setExpandedLead(isExpanded ? null : lead._id)}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          {/* Tag badge */}
                          <span 
                            className="px-2 py-1 rounded-full text-xs font-medium"
                            style={{ backgroundColor: `${tagConfig.color}20`, color: tagConfig.color }}
                          >
                            {tagConfig.emoji} {tagConfig.label}
                          </span>
                          
                          {/* Name & Email */}
                          <div className="min-w-0 flex-1">
                            <h3 className="font-heading font-semibold text-white truncate">{lead.name}</h3>
                            <p className="text-white/40 text-sm truncate">{lead.email}</p>
                          </div>
                          
                          {/* Status badge */}
                          <span 
                            className={`px-3 py-1 rounded-full text-xs font-medium ${statusConfig.bgColor}`}
                            style={{ color: statusConfig.color }}
                          >
                            {statusConfig.label}
                          </span>
                          
                          {/* Language */}
                          {lead.language && (
                            <span className="text-white/40 text-xs uppercase">{lead.language}</span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Quick WhatsApp button */}
                          <a
                            href={lead.whatsapp_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="p-2 rounded-lg bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 transition-colors"
                            title="Message on WhatsApp"
                          >
                            <MessageCircle size={18} />
                          </a>
                          
                          {isExpanded ? <ChevronUp size={18} className="text-white/40" /> : <ChevronDown size={18} className="text-white/40" />}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="border-t border-white/10 p-4 bg-white/5">
                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Left column: Form answers */}
                          <div className="space-y-4">
                            <h4 className="font-heading font-semibold text-white text-sm mb-3">Form Answers</h4>
                            
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <span className="text-white/40">Phone:</span>
                                <p className="text-white">{lead.phone}</p>
                              </div>
                              <div>
                                <span className="text-white/40">Platform:</span>
                                <p className="text-white">{lead.platform || '-'}</p>
                              </div>
                              <div>
                                <span className="text-white/40">Trading Location:</span>
                                <p className="text-white">{lead.tradingLocation || '-'}</p>
                              </div>
                              <div>
                                <span className="text-white/40">Timeline:</span>
                                <p className="text-white">{lead.timeline?.replace('_', ' ') || '-'}</p>
                              </div>
                              <div>
                                <span className="text-white/40">Capital > $3k:</span>
                                <p className="text-white">
                                  {lead.capitalAbove3k === 'amount' 
                                    ? `$${lead.capitalAmount}` 
                                    : lead.capitalAbove3k || '-'}
                                </p>
                              </div>
                              <div>
                                <span className="text-white/40">Prop Size:</span>
                                <p className="text-white">{lead.propAccountSize || '-'}</p>
                              </div>
                              <div>
                                <span className="text-white/40">Tried Tester:</span>
                                <p className="text-white">{lead.triedTester?.replace('_', ' ') || '-'}</p>
                              </div>
                              <div>
                                <span className="text-white/40">Created:</span>
                                <p className="text-white text-xs">
                                  {lead.created_at ? new Date(lead.created_at).toLocaleString() : '-'}
                                </p>
                              </div>
                            </div>

                            {lead.mainQuestion && (
                              <div className="mt-4">
                                <span className="text-white/40 text-sm">Main Question:</span>
                                <p className="text-white text-sm mt-1 p-3 rounded-lg bg-white/5">{lead.mainQuestion}</p>
                              </div>
                            )}

                            {lead.auto_notes?.length > 0 && (
                              <div className="mt-4">
                                <span className="text-[#F59E0B] text-xs">Auto Notes:</span>
                                <ul className="mt-1 space-y-1">
                                  {lead.auto_notes.map((note, i) => (
                                    <li key={i} className="text-[#F59E0B] text-sm">• {note}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {/* WhatsApp Opened Indicator */}
                            {lead.status === 'qualified_awaiting' && (
                              <div className="mt-4 p-3 rounded-lg bg-white/5 border border-white/10">
                                <div className="flex items-center gap-2">
                                  <span className="text-white/40 text-sm">WhatsApp opened:</span>
                                  {lead.whatsapp_opened ? (
                                    <span className="text-[#25D366] text-sm font-medium flex items-center gap-1">
                                      <CheckCircle size={14} /> Yes
                                    </span>
                                  ) : (
                                    <span className="text-[#EF4444] text-sm font-medium flex items-center gap-1">
                                      <XCircle size={14} /> No
                                    </span>
                                  )}
                                </div>
                                {lead.whatsapp_opened && lead.whatsapp_opened_at && (
                                  <p className="text-white/30 text-xs mt-1">
                                    Opened: {new Date(lead.whatsapp_opened_at).toLocaleString()}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Right column: Actions */}
                          <div className="space-y-4">
                            {/* Call Time Field (for qualified_awaiting leads) */}
                            {(lead.status === 'qualified_awaiting' || lead.status === 'booked') && (
                              <div>
                                <h4 className="font-heading font-semibold text-white text-sm mb-3">
                                  Call Time {lead.status === 'booked' && <span className="text-[#10B981]">(Booked)</span>}
                                </h4>
                                <input
                                  type="datetime-local"
                                  defaultValue={lead.call_time ? new Date(lead.call_time).toISOString().slice(0, 16) : ''}
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      setCallTime(lead._id, new Date(e.target.value).toISOString());
                                    }
                                  }}
                                  className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-[#7c3aed]/50"
                                  data-testid={`call-time-${lead._id}`}
                                />
                                {lead.call_time && (
                                  <p className="text-white/40 text-xs mt-2">
                                    Scheduled: {new Date(lead.call_time).toLocaleString()}
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {/* Outcome buttons (if call is booked/completed) */}
                            {(lead.status === 'booked' || lead.status === 'completed' || lead.status === 'no_show') && (
                              <div>
                                <h4 className="font-heading font-semibold text-white text-sm mb-3">Call Outcome</h4>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => updateOutcome(lead._id, 'interested')}
                                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                                      lead.outcome === 'interested'
                                        ? 'bg-[#10B981] text-white'
                                        : 'bg-[#10B981]/20 text-[#10B981] hover:bg-[#10B981]/30'
                                    }`}
                                  >
                                    <UserCheck size={14} className="inline mr-1" />
                                    Interested
                                  </button>
                                  <button
                                    onClick={() => updateOutcome(lead._id, 'not_now')}
                                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                                      lead.outcome === 'not_now'
                                        ? 'bg-[#06b6d4] text-white'
                                        : 'bg-[#06b6d4]/20 text-[#06b6d4] hover:bg-[#06b6d4]/30'
                                    }`}
                                  >
                                    <Clock size={14} className="inline mr-1" />
                                    Not Now
                                  </button>
                                  <button
                                    onClick={() => updateOutcome(lead._id, 'no_show')}
                                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${
                                      lead.outcome === 'no_show'
                                        ? 'bg-[#F59E0B] text-white'
                                        : 'bg-[#F59E0B]/20 text-[#F59E0B] hover:bg-[#F59E0B]/30'
                                    }`}
                                  >
                                    <XCircle size={14} className="inline mr-1" />
                                    No-Show
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* WhatsApp Actions */}
                            <div>
                              <h4 className="font-heading font-semibold text-white text-sm mb-3">WhatsApp</h4>
                              <div className="space-y-2">
                                <a
                                  href={lead.whatsapp_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/30 transition-all text-sm font-medium"
                                >
                                  <MessageCircle size={16} />
                                  Message on WhatsApp
                                </a>
                                {lead.booking && (
                                  <a
                                    href={generateWhatsAppReminder(lead)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-lg bg-[#7c3aed]/20 text-[#7c3aed] hover:bg-[#7c3aed]/30 transition-all text-sm font-medium"
                                  >
                                    <Send size={16} />
                                    Send WhatsApp Reminder
                                  </a>
                                )}
                              </div>
                            </div>

                            {/* Manual Notes */}
                            <div>
                              <h4 className="font-heading font-semibold text-white text-sm mb-3">Notes</h4>
                              <textarea
                                defaultValue={lead.manual_notes || ''}
                                onBlur={(e) => updateNotes(lead._id, e.target.value)}
                                placeholder="Add notes..."
                                rows={3}
                                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#7c3aed]/50 resize-none"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                );
              })
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
